import { Calendar, MapPin, Smartphone } from "lucide-react";

// Sprint 09J — the new "Overzicht" section: a compact landing summary for an
// existing event, not a re-render of every setting. Uses ONLY data the parent
// AdminEventWorkspace already fetched via GET /admin/events (which already embeds
// organizations(...) and ticket_types(id,capacity,sold_count,active)) — no new
// backend endpoint, no new analytics engine, per the sprint's explicit scope limit.

const STATUS_LABELS = {
  draft: "Concept",
  published: "Gepubliceerd",
  sold_out: "Uitverkocht",
  cancelled: "Geannuleerd",
  archived: "Gearchiveerd",
};

const STATUS_STYLES = {
  draft: "bg-slate-500/15 text-slate-300",
  published: "bg-emerald-500/15 text-emerald-400",
  sold_out: "bg-amber-500/15 text-amber-400",
  cancelled: "bg-red-500/15 text-red-400",
  archived: "bg-slate-500/15 text-slate-400",
};

function formatDateRange(startsAt, endsAt) {
  if (!startsAt) return "";
  const opts = { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" };
  const start = new Intl.DateTimeFormat("nl-NL", opts).format(new Date(startsAt));
  if (!endsAt) return start;
  const end = new Intl.DateTimeFormat("nl-NL", opts).format(new Date(endsAt));
  return `${start} – ${end}`;
}

function NavCard({ icon: Icon, label, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left flex items-center gap-3 p-4 rounded-xl border border-svj-border bg-svj-bg/60 hover:border-svj-cyan/50 transition-colors"
    >
      <div className="h-9 w-9 rounded-lg bg-svj-cyan/10 text-svj-cyan flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{label}</div>
        {hint && <div className="text-xs text-slate-400 truncate">{hint}</div>}
      </div>
    </button>
  );
}

export default function AdminEventOverview({ form, rawEvent, orgName, sections, onNavigate }) {
  const ticketTypes = rawEvent?.ticket_types || [];
  const ticketTypeCount = ticketTypes.length;
  const totalSold = ticketTypes.reduce((sum, tt) => sum + (tt.sold_count || 0), 0);
  const totalCapacity = ticketTypes.reduce((sum, tt) => sum + (tt.capacity || 0), 0);

  return (
    <div className="space-y-6">
      <div className="bg-svj-card border border-svj-border rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-5">
          {form.cover_image_url ? (
            <img
              src={form.cover_image_url}
              alt=""
              className="w-full sm:w-48 aspect-[16/9] object-cover rounded-xl border border-svj-border shrink-0"
            />
          ) : (
            <div className="w-full sm:w-48 aspect-[16/9] rounded-xl border border-dashed border-svj-border flex items-center justify-center text-xs text-slate-500 shrink-0">
              Geen cover
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[form.status] || "bg-slate-500/15 text-slate-300"}`}>
                {STATUS_LABELS[form.status] || form.status}
              </span>
              {form.featured && (
                <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-svj-orange/15 text-svj-orange">
                  Uitgelicht
                </span>
              )}
              {form.companion_app_enabled && (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-svj-cyan/15 text-svj-cyan">
                  <Smartphone className="h-3 w-3" /> StapKompas actief
                </span>
              )}
            </div>
            <h2 className="text-xl font-extrabold text-white truncate">{form.title || "Naamloos event"}</h2>
            <div className="text-sm text-slate-300 space-y-1">
              {form.starts_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  {formatDateRange(form.starts_at, form.ends_at)}
                </div>
              )}
              {form.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  {form.location}
                </div>
              )}
              {orgName && <div className="text-xs text-slate-500">Organisatie: {orgName}</div>}
            </div>
          </div>
        </div>

        {ticketTypeCount > 0 && (
          <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-svj-border">
            <div className="text-center">
              <div className="text-xl font-extrabold text-white">{ticketTypeCount}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">Tickettypes</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-extrabold text-white">{totalSold}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">Verkocht</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-extrabold text-white">{totalCapacity || "∞"}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">Capaciteit</div>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Snel naar</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(sections || [])
            .filter((s) => s.key !== "overview")
            .map((s) => (
              <NavCard key={s.key} icon={s.icon} label={s.label} onClick={() => onNavigate(s.key)} />
            ))}
        </div>
      </div>
    </div>
  );
}
